import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils.js';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--radius-element,var(--radius))] text-sm font-semibold transition-all duration-150 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring,var(--color-accent))] cursor-pointer select-none',
  {
    variants: {
      variant: {
        default: 'bg-[var(--primary,var(--color-accent))] text-[var(--on-primary,var(--color-on-accent))] shadow-sm hover:brightness-110 active:brightness-95',
        secondary: 'bg-[var(--secondary-container,var(--color-background-muted))] text-[var(--foreground,var(--color-text-primary))] hover:bg-[var(--surface-container-high)]',
        outline: 'border border-[var(--border,var(--color-border))] bg-white text-[var(--foreground,var(--color-text-primary))] hover:bg-[var(--surface-container-low)] hover:border-[var(--outline,var(--color-border-emphasized))]',
        ghost: 'text-[var(--foreground,var(--color-text-primary))] hover:bg-[var(--surface-container,var(--color-background-muted))]',
        subtle: 'border border-[var(--border,var(--color-border))] bg-[var(--surface-container-lowest,var(--color-background-surface))] text-[var(--foreground,var(--color-text-primary))] hover:bg-white',
        destructive: 'bg-[var(--destructive,var(--color-error))] text-white shadow-sm hover:brightness-110 active:brightness-95',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-11 px-6 text-base',
        icon: 'h-10 w-10 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      data-astryx-btn=""
      {...props}
    />
  );
});

Button.displayName = 'Button';

export { Button, buttonVariants };
